import { createTheme } from '@mui/material/styles';

import { getDesignTokens } from './core';
import type { AccentPalette } from './core/color';
import {
  HORIZONTAL_SCROLL_CONTAINERS,
  visibleScrollbarStyles,
} from './scrollbar-styles';

export type ThemeMode = 'light' | 'dark';
export type { AccentPalette } from './core/color';

declare module '@mui/material/styles' {
  interface Theme {
    accentPalette: AccentPalette;
  }
  interface ThemeOptions {
    accentPalette?: AccentPalette;
  }
  interface TypographyVariants {
    kpiLabel: React.CSSProperties;
    kpiValue: React.CSSProperties;
    sectionLabel: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    kpiLabel?: React.CSSProperties;
    kpiValue?: React.CSSProperties;
    sectionLabel?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    kpiLabel: true;
    kpiValue: true;
    sectionLabel: true;
  }
}

const DEFAULT_ACCENT: AccentPalette = 'clockwork';

/**
 * Creates the app theme for the given mode and accent palette.
 * Use in ThemeProvider; theme updates when mode or accent changes.
 */
export function createAppTheme(
  mode: ThemeMode,
  accent: AccentPalette = DEFAULT_ACCENT
): ReturnType<typeof createTheme> {
  const t = getDesignTokens(mode, accent);
  const isDark = mode === 'dark';

  return createTheme({
    accentPalette: accent,
    palette: {
      mode: isDark ? 'dark' : 'light',
      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
        disabled: isDark ? `${t.textSecondary}80` : `${t.textPrimary}80`,
      },
      action: {
        disabledOpacity: 0.5,
        activatedOpacity: 0.8,
      },
      primary: {
        // Use unified accent color for interactive elements (buttons, checkboxes, etc.)
        main: t.accent,
        contrastText: t.brand.primaryContrast,
      },
      secondary: {
        main: t.textSecondary,
        contrastText: t.textPrimary,
      },
      success: {
        // Use risk.low (teal-blue) for consistency with PolicyAlignment/PolicyRisk
        main: t.risk.low,
        contrastText: t.brand.primaryContrast,
      },
      error: {
        main: t.status.danger,
        contrastText: t.brand.primaryContrast,
      },
      info: {
        main: t.status.info,
        contrastText: t.brand.primaryContrast,
      },
      warning: {
        main: t.status.warning,
        contrastText: t.brand.primaryContrast,
      },
      background: {
        default: t.canvasBg,
        paper: t.surface1,
      },
      divider: t.borderSubtle,
    },
    shape: {
      borderRadius: t.radiusMedium,
    },
    spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 768,
        md: 1280,
        lg: 1512,
        xl: 1728,
      },
    },
    typography: {
      allVariants: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontStyle: 'normal',
        color: t.textPrimary,
      },
      // Editorial page title – serif (use sparingly)
      h1: {
        fontFamily: `'Cormorant Garamond', serif`,
        fontWeight: 500,
        fontSize: '1.75rem',
        lineHeight: 1.2,
      },
      // Section heading – sans-serif for UI consistency
      h2: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.2,
      },
      h3: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.2,
      },
      h4: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.2,
      },
      h5: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1,
      },
      body1: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.3,
      },
      body2: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 400,
        fontStyle: 'italic',
        fontSize: '1rem',
        lineHeight: 1.2,
      },
      button: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
      },
      kpiLabel: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '0.8125rem',
        letterSpacing: '0.06em',
        textTransform: 'none' as const,
        lineHeight: 1.2,
      },
      kpiValue: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 700,
        fontSize: '1.5rem',
        letterSpacing: 0,
        textTransform: 'none' as const,
        lineHeight: 1.2,
      },
      sectionLabel: {
        fontFamily: `'Nunito Sans', sans-serif`,
        fontWeight: 600,
        fontSize: '0.875rem',
        letterSpacing: '0.02em',
        textTransform: 'none' as const,
        lineHeight: 1.2,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.canvasBg,
            colorScheme: isDark ? 'dark' : 'light',
          },
          [HORIZONTAL_SCROLL_CONTAINERS]: visibleScrollbarStyles(t.textMuted),
        },
      },
      // body2 is italic for prose, and table cells take body2 by default, so they must opt out.
      MuiTableCell: {
        styleOverrides: {
          root: { fontStyle: 'normal' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: t.radiusMedium,
            padding: '10px 26px',
            fontFamily: `'Nunito Sans', sans-serif`,
            fontWeight: 600,
            fontSize: '14px',
            textTransform: 'none',
            lineHeight: '20px',
            // Unified accent focus ring
            '&:focus-visible': {
              outline: 'none',
              boxShadow: t.focusRing,
            },
          },
          // Premium contained button styling - deeper color for better contrast (solid, never transparent)
          contained: {
            backgroundColor: `${t.buttonPrimary} !important`,
            color: t.buttonPrimaryText,
            fontWeight: 700,
            letterSpacing: '0.02em',
            boxShadow: isDark
              ? '0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)'
              : '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
            '&:hover': {
              backgroundColor: `${t.buttonPrimaryHover} !important`,
              boxShadow: isDark
                ? '0 4px 8px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3)'
                : '0 4px 8px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
            },
            '&:active': {
              boxShadow: isDark
                ? '0 1px 2px rgba(0,0,0,0.3)'
                : '0 1px 2px rgba(0,0,0,0.1)',
            },
            // Readable disabled state: muted solid fill + legible text instead
            // of a solid brand fill with barely-visible faded text. The
            // `!important` beats the forced brand background above.
            '&.Mui-disabled': {
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.10) !important'
                : 'rgba(15,23,42,0.06) !important',
              color: t.textSecondary,
              boxShadow: 'none',
            },
          },
          // Outlined: in light mode use link color (deeper teal) for readability on white; dark mode keeps accent
          outlined: {
            borderColor: isDark ? t.accent : t.link,
            color: isDark ? t.accent : t.link,
            '&:hover': {
              borderColor: isDark ? t.accent : t.link,
              backgroundColor: t.surface2,
            },
            '&.Mui-disabled': {
              color: t.textSecondary,
              borderColor: t.borderDefault,
            },
          },
          // Text: same as outlined for readability on white in light mode
          text: {
            color: isDark ? t.accent : t.link,
            '&:hover': {
              backgroundColor: t.surface2,
            },
            '&.Mui-disabled': {
              color: t.textSecondary,
            },
          },
          // Force solid hover for primary contained (beats MUI's color variant specificity)
          containedPrimary: {
            backgroundColor: `${t.buttonPrimary} !important`,
            '&:hover': {
              backgroundColor: `${t.buttonPrimaryHover} !important`,
            },
            '&.Mui-disabled': {
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.10) !important'
                : 'rgba(15,23,42,0.06) !important',
              color: t.textSecondary,
              boxShadow: 'none',
            },
          },
          sizeSmall: {
            padding: '6px 20px',
          },
          sizeMedium: {
            padding: '10px 26px',
          },
          sizeLarge: {
            padding: '12px 32px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // Unified accent focus ring
            '&:focus-visible': {
              outline: 'none',
              boxShadow: t.focusRing,
            },
          },
          sizeSmall: {
            padding: '6px',
            '.MuiSvgIcon-root': { fontSize: '16px' },
          },
          sizeMedium: {
            padding: '8px',
            '.MuiSvgIcon-root': { fontSize: '16px' },
          },
          sizeLarge: {
            padding: '6px',
            '.MuiSvgIcon-root': { fontSize: '24px' },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: t.link,
            '&:hover': {
              color: t.link,
              backgroundColor: t.linkHover,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface1,
            // Solid background in dark mode (no gradient) for premium look
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(${t.surface1}, ${t.surface1})`,
            borderRadius: t.radiusLarge,
            // Hairline border at 6% white in dark mode
            border: `1px solid ${isDark ? t.borderSubtle : t.borderDefault}`,
            // Soft shadow + faint top highlight for subtle elevation
            boxShadow: isDark
              ? `${t.shadowSoft}, inset 0 1px 0 ${t.hairline}`
              : t.shadowSoft,
            transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
            // Subtle surface overlay on hover, no dramatic changes
            '&:hover': {
              backgroundColor: isDark
                ? `color-mix(in srgb, ${t.surface1} 98%, white 2%)`
                : t.surfaceOverlay,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface1,
            // Solid background in dark mode (no gradient) for premium look
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(${t.surface1}, ${t.surface1})`,
            borderRadius: t.radiusLarge,
            // Hairline border at 6% white in dark mode
            border: `1px solid ${isDark ? t.borderSubtle : t.borderDefault}`,
            // Soft shadow + faint top highlight for subtle elevation
            boxShadow: isDark
              ? `${t.shadowSoft}, inset 0 1px 0 ${t.hairline}`
              : t.shadowSoft,
            transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
            // Subtle surface overlay on hover, no dramatic changes
            '&:hover': {
              backgroundColor: isDark
                ? `color-mix(in srgb, ${t.surface1} 98%, white 2%)`
                : t.surfaceOverlay,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '24px 24px 0 24px' },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: '24px 24px 0 24px' },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: { padding: '24px' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 400,
            color: t.textSecondary,
            '&.Mui-selected': { color: t.textPrimary },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: t.form.inputLabel,
          },
          outlined: {
            '&.Mui-focused': {
              color: t.form.inputLabel,
            },
            // Ensure shrunk label (floated above the field) covers the border line
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
              backgroundColor: t.surface1,
              paddingLeft: '4px',
              paddingRight: '4px',
              zIndex: 1, // Must be higher than the input root to actally overlap the fieldset
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: t.radiusSmall,
            // Solid background in both modes so hover never makes the field look transparent (e.g. in dialogs)
            backgroundColor: `${t.surface1} !important`,
            '&:hover': {
              backgroundColor: `${t.surface1} !important`,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: t.borderDefault,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.accent,
              borderWidth: '1px',
            },
            // Unified accent focus ring with soft glow
            '&.Mui-focused': {
              boxShadow: t.focusRing,
            },
            // Ensure typed input text and placeholder are theme-aware (e.g. sign-in in dark mode)
            '& .MuiInputBase-input': {
              color: t.textPrimary,
              '&::placeholder': {
                color: t.textPlaceholder,
                opacity: 1,
              },
              // Override browser autofill background so it doesn't show light blue in dark mode
              ...(isDark && {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 100px ${t.surface1} inset`,
                  caretColor: t.textPrimary,
                },
                '&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
                  {
                    WebkitBoxShadow: `0 0 0 100px ${t.surface1} inset`,
                  },
              }),
              // Date picker: make native calendar icon visible in dark mode (otherwise it's dark on dark)
              ...(isDark && {
                '&[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  opacity: 0.85,
                },
              }),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.08)'
              : t.status.neutralBg,
            color: t.textPrimary,
            borderRadius: t.radiusPill,
            fontSize: 13,
            '#navigation-column &': {
              backgroundColor: t.nav.badgeMutedBg,
              color: t.nav.itemTextOnDark,
            },
            '#navigation-column &.MuiChip-colorSuccess': {
              backgroundColor: t.status.success,
              color: t.brand.primaryContrast,
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            // Opaque enough that content behind doesn’t show through (fixes “transparent on hover” in dark mode)
            backgroundColor: isDark
              ? 'rgba(0, 0, 0, 0.8)'
              : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: `${t.surface1} !important`,
            backgroundColor: `${t.surface1} !important`,
            borderRadius: t.radiusLarge,
            boxShadow: t.shadowMedium,
            // Prevent any hover from making the dialog transparent
            '&:hover': {
              background: `${t.surface1} !important`,
              backgroundColor: `${t.surface1} !important`,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            background: `${t.surface1} !important`,
            backgroundColor: `${t.surface1} !important`,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            background: `${t.surface1} !important`,
            backgroundColor: `${t.surface1} !important`,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            background: `${t.surface1} !important`,
            backgroundColor: `${t.surface1} !important`,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface2,
            backgroundImage: `linear-gradient(${t.surface2}, ${t.surface2})`,
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface1,
            backgroundImage: `linear-gradient(${t.surface1}, ${t.surface1})`,
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface1,
            backgroundImage: `linear-gradient(${t.surface1}, ${t.surface1})`,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            ...(isDark && {
              backgroundColor: `${t.surface2} !important`,
            }),
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            font: '14px',
            fontWeight: 700,
            backgroundColor: t.surface1,
            color: t.textPrimary,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: `${t.accent}1F`,
              color: t.accent,
              '&:hover': {
                backgroundColor: `${t.accent}29`,
              },
            },
            '&:hover': {
              backgroundColor: `${t.accent}0F`,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? t.surface2 : '#1A2024',
            color: isDark ? t.textPrimary : '#FFFFFF',
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: t.radiusSmall,
            padding: '6px 10px',
            border: isDark ? `1px solid ${t.borderSubtle}` : 'none',
          },
          arrow: {
            color: isDark ? t.surface2 : '#1A2024',
          },
        },
      },
    },
  });
}

/** Pre-built themes for backward compatibility. Prefer createAppTheme(mode, accent) in app root. */
export const lightTheme = createAppTheme('light', DEFAULT_ACCENT);
export const darkTheme = createAppTheme('dark', DEFAULT_ACCENT);
